/**
 * KpiBandSection — a single 4-cell KPI row on the light page as a plain OUTLINE
 * table: one outer 2px black box (NO fill, NO shadow) split into 4 cells by
 * vertical black dividers. Each cell stacks a big `font-head` number (colorless —
 * neutral `text-foreground`) over a small `font-mono` uppercase label.
 *
 * Three figures are REAL — wired from `usePublicStats()` (GET /api/public/landing-stats,
 * seeded so it never flashes blanks); ACTIVE STUDIES is a FIXED constant (6). All four
 * count up from 0 → their value via CountUp once the band scrolls into view (reduced-
 * motion aware — they render the final value immediately when reduced motion is preferred):
 *   PARTICIPANTS  → totals.participants   (endpoint)
 *   ACTIVE STUDIES→ 6                      (fixed constant)
 *   RECORDS       → totals.records        (endpoint)
 *   DAYS OF DATA  → span.days             (endpoint)
 *
 * Preflight is OFF, so the divider/box borders are declared `border-solid`
 * explicitly. ZERO MUI.
 */
import * as React from 'react';
import usePublicStats from '../../../hooks/usePublicStats';
import CountUp from './CountUp';
import StatCell from './shared/StatCell';

/** One KPI cell — every figure animates (CountUp from 0 → value). */
interface Kpi {
  label: string;
  value: number;
  /** Tailwind text-color class for the number (neutral — colorless outline table). */
  colorClass: string;
}

const KpiBandSection: React.FC = () => {
  const { data } = usePublicStats();

  const kpis: Kpi[] = [
    { label: 'Participants', value: data.totals.participants, colorClass: 'text-foreground' },
    { label: 'Active Studies', value: 6, colorClass: 'text-foreground' }, // FIXED at 6 — not endpoint-driven
    { label: 'Daily Records', value: data.totals.records, colorClass: 'text-foreground' },
    { label: 'Days of Data', value: data.span.days, colorClass: 'text-foreground' },
  ];

  return (
    <section className="bg-background px-4 py-16 md:px-8 md:py-20">
      <div className="mx-auto max-w-6xl">
        {/* The band as a plain OUTLINE table: one 2px black box, no fill, no
            shadow, split into 4 cells. 4 cols at md+, 2×2 on mobile. Vertical
            black dividers per-cell via left borders (suppressed at row starts). */}
        <div className="grid grid-cols-2 border-2 border-solid border-black md:grid-cols-4">
          {kpis.map((kpi, i) => (
            <StatCell
              key={kpi.label}
              label={kpi.label}
              valueClassName={kpi.colorClass}
              size="lg"
              className={[
                'px-4 py-8 md:py-10',
                // Vertical divider on the left edge of every cell except the first
                // in each row. Mobile (2 cols): suppress on cols 0 & 2. md+ (4
                // cols): suppress only on col 0.
                i % 2 === 0 ? '' : 'border-l-2 border-solid border-black',
                i % 4 === 0 ? 'md:border-l-0' : 'md:border-l-2 md:border-solid md:border-black',
                // On mobile the bottom row needs a divider above it; add a top
                // border to the second row (indices 2,3) and remove it at md+.
                i >= 2 ? 'border-t-2 border-solid border-black md:border-t-0' : '',
              ].join(' ')}
            >
              <CountUp value={kpi.value} />
            </StatCell>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KpiBandSection;
